import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { APP_NAME } from '../../lib/appName';
import type { HtmlImportReport, RehostItemResult } from '../../types/api';
import type { RehostRun } from './useRehostImages';

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** How many images would be copied, and where they are now. */
export function copyableImages(images: HtmlImportReport['images']) {
  const embedded = images.base64;
  const atMailchimp = images.mailchimpHosted;
  const other = Math.max(0, images.hosted - images.ownHosted - images.mailchimpHosted);
  return { embedded, atMailchimp, other, total: embedded + atMailchimp + other };
}

function ResultRow({ r }: { r: RehostItemResult }) {
  const ok = r.status === 'copied';
  return (
    <li className="flex gap-2">
      <span aria-hidden="true" className={ok ? 'text-success-text' : 'text-danger-text'}>
        {ok ? '✓' : '!'}
      </span>
      <span className="min-w-0 flex-grow">
        <span className="block break-all">
          {r.label}
          {r.alt ? <span className="text-muted">{` (alt text: ${r.alt})`}</span> : null}
        </span>
        <span className={ok ? 'text-muted' : 'text-danger-text'}>
          {ok
            ? `Copied${r.byteSize ? `, ${formatBytes(r.byteSize)}` : ''}${r.created === false ? ' (already in the image library)' : ''}.`
            : `Not copied: ${r.message ?? 'unknown reason'} It still points at the original.`}
        </span>
      </span>
    </li>
  );
}

/**
 * The "Copy all to NP-Flow" step of the Images section: how many images are
 * embedded or stored at Mailchimp, one button, the progress while the server
 * works in rounds, then each image with its result and the reason for each
 * one that could not be copied. Display and one button only: the copying
 * and every rule are the server's.
 */
export function CopyImagesPanel({
  images,
  run,
  onCopy,
  disabled = false,
}: {
  images: HtmlImportReport['images'];
  run: RehostRun;
  onCopy: () => void;
  /** True while the check is out of date, or the caller does not allow it. */
  disabled?: boolean;
}) {
  const counts = copyableImages(images);
  const running = run.status === 'running';
  const handled = run.results.length;
  const total = handled + (run.remaining ?? 0);
  const copied = run.results.filter((r) => r.status === 'copied').length;
  const failed = handled - copied;

  if (counts.total === 0 && handled === 0 && run.status === 'idle') {
    return images.ownHosted > 0 ? (
      <p className="m-0 text-success-text">
        All {plural(images.ownHosted, 'image is', 'images are')} on our own media domain.
      </p>
    ) : null;
  }

  const where = [
    counts.embedded > 0 ? `${counts.embedded} embedded in the HTML` : null,
    counts.atMailchimp > 0 ? `${counts.atMailchimp} stored at Mailchimp` : null,
    counts.other > 0 ? `${counts.other} on other hosts` : null,
  ].filter((p): p is string => p !== null);

  return (
    <div className="mt-1 flex flex-col gap-2 rounded-xl border border-border bg-bg p-3">
      {counts.total > 0 ? (
        <p className="m-0">
          <strong>
            {plural(counts.total, 'image is', 'images are')} not on our own media domain
          </strong>{' '}
          ({where.join(', ')}). Copy them to {APP_NAME} so the newsletter does not depend on them
          and the HTML gets small.
          {counts.other > 0 ? ' Images on other hosts are not copied; they are named below.' : ''}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={onCopy} disabled={disabled || running || counts.total === 0}>
          {running ? 'Copying…' : `Copy all to ${APP_NAME}`}
        </Button>
        {running ? (
          <span role="status" className="text-muted">
            {total > 0 ? `${handled} of ${total} handled…` : 'Starting…'}
          </span>
        ) : null}
      </div>
      {running && total > 0 ? (
        <div
          role="progressbar"
          aria-label="Copying images"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={handled}
          className="h-1.5 w-full overflow-hidden rounded-full bg-border"
        >
          <div className="h-full bg-accent" style={{ width: `${(handled / total) * 100}%` }} />
        </div>
      ) : null}

      {run.error ? <Alert>{run.error.message}</Alert> : null}

      {run.status === 'done' || (run.status === 'error' && handled > 0) ? (
        <p className="m-0" role="status">
          {copied > 0 ? `${plural(copied, 'image', 'images')} copied. ` : ''}
          {failed > 0 ? `${plural(failed, 'image', 'images')} could not be copied.` : ''}
          {handled === 0 ? 'No images were copied.' : ''}
        </p>
      ) : null}

      {handled > 0 ? (
        <ul className="m-0 flex max-h-56 list-none flex-col gap-2 overflow-y-auto p-0 text-[13px]">
          {[...run.results]
            .sort((a, b) => Number(a.status === 'copied') - Number(b.status === 'copied'))
            .map((r) => (
              <ResultRow key={r.key} r={r} />
            ))}
        </ul>
      ) : null}
    </div>
  );
}
